import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { CustomerDetail } from "../../components/CustomerDetail";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const EMPTY = { name: "", phone: "", email: "", address: "", municipality: "", posto: "", suco: "", aldeia: "", id_type: "BI", id_number: "", notes: "", customer_kind: "individual", nationality: "", marital_status: "", profession: "", nif: "", company_reg_no: "", legal_form: "", rep_name: "", rep_id_number: "", rep_position: "" };
const COMPANY_KEYS = ["customer_kind", "nationality", "marital_status", "profession", "nif", "company_reg_no", "legal_form", "rep_name", "rep_id_number", "rep_position"];
const ID_TYPES = ["BI", "Passport", "Electoral", "Driver's Licence"];

export default function Customers() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/customers", { params: q ? { q } : {} }).then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setPhoto(null); setPhotoUrl(null); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", municipality: c.municipality || "", posto: c.posto || "", suco: c.suco || "", aldeia: c.aldeia || "", id_type: c.id_type || "BI", id_number: c.id_number || "", notes: c.notes || "", ...Object.fromEntries(COMPANY_KEYS.map((k) => [k, c[k] || EMPTY[k]])) }); setPhoto(null); setPhotoUrl(null); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let cid = editing?.id;
      if (editing) await API.patch(`/shop/customers/${editing.id}`, form);
      else { const { data } = await API.post("/shop/customers", form); cid = data.id; }
      if (photo && cid) {
        const fd = new FormData();
        fd.append("file", photo);
        await API.post(`/shop/customers/${cid}/photo`, fd);
      }
      toast.success(t("msg.saved"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`${t("common.delete")} ${c.name}?`)) return;
    try {
      await API.delete(`/shop/customers/${c.id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("customers.title")}</h1>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="customer-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("common.search")} className="pl-9 w-56 bg-white" />
          </div>
          <Button data-testid="new-customer-button" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" />{t("customers.new")}
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.phone")}</TableHead>
              <TableHead>{t("customers.email")}</TableHead>
              <TableHead>{t("customers.id_number")}</TableHead>
              <TableHead>{t("customers.loans")}</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {customers.map((c) => (
              <TableRow key={c.id} data-testid={`customer-row-${c.id.slice(0, 8)}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2.5">
                    {c.photo_path ? (
                      <img src={`${API.defaults.baseURL}/shop/customers/${c.id}/photo`} alt="" data-testid={`customer-avatar-${c.id.slice(0, 8)}`}
                        className="h-8 w-8 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500">
                        {c.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    {c.name}
                  </div>
                </TableCell>
                <TableCell>{c.phone || "—"}</TableCell>
                <TableCell className="text-sm">{c.email || "—"}</TableCell>
                <TableCell className="font-mono text-sm">{c.id_number ? `${c.id_type || "BI"} · ${c.id_number}` : "—"}</TableCell>
                <TableCell>{c.loan_count}</TableCell>
                <TableCell className="text-right">
                  <button data-testid={`view-customer-${c.id.slice(0, 8)}`} onClick={() => setViewing(c)} className="p-2 hover:bg-slate-100 rounded-lg" title={t("cust.view")}><Eye className="h-4 w-4 text-slate-600" /></button>
                  <button data-testid={`edit-customer-${c.id.slice(0, 8)}`} onClick={() => openEdit(c)} className="p-2 hover:bg-slate-100 rounded-lg"><Pencil className="h-4 w-4 text-slate-600" /></button>
                  {managerUp && (
                    <button data-testid={`delete-customer-${c.id.slice(0, 8)}`} onClick={() => remove(c)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4 text-red-600" /></button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("common.edit") : t("customers.new")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="customer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("cust.kind")}</Label>
                <Select value={form.customer_kind} onValueChange={(v) => setForm({ ...form, customer_kind: v })}>
                  <SelectTrigger data-testid="customer-kind-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="individual" data-testid="customer-kind-individual">{t("ct.individual")}</SelectItem>
                    <SelectItem value="company" data-testid="customer-kind-company">{t("ct.company")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("cust.nif")}</Label>
                <Input data-testid="customer-nif-input" value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="mt-1" />
              </div>
            </div>
            {form.customer_kind === "company" ? (
              <div className="grid grid-cols-2 gap-3 border border-slate-200 rounded-lg p-3" data-testid="customer-company-fields">
                <div>
                  <Label className="text-xs">{t("cust.reg_no")}</Label>
                  <Input data-testid="customer-reg-input" value={form.company_reg_no} onChange={(e) => setForm({ ...form, company_reg_no: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("cust.legal_form")}</Label>
                  <Input value={form.legal_form} placeholder="Unipessoal Lda., Lda., S.A." onChange={(e) => setForm({ ...form, legal_form: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("cust.rep_name")}</Label>
                  <Input data-testid="customer-rep-input" value={form.rep_name} onChange={(e) => setForm({ ...form, rep_name: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("cust.rep_id")}</Label>
                  <Input value={form.rep_id_number} onChange={(e) => setForm({ ...form, rep_id_number: e.target.value })} className="mt-1 h-9" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{t("cust.rep_position")}</Label>
                  <Input value={form.rep_position} placeholder="Administrador / Gerente" onChange={(e) => setForm({ ...form, rep_position: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{t("cust.nationality")}</Label>
                  <Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("cust.marital")}</Label>
                  <Input value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">{t("cust.profession")}</Label>
                  <Input value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("common.phone")}</Label>
                <Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("customers.email")}</Label>
                <Input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("customers.id_type")}</Label>
                <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
                  <SelectTrigger data-testid="customer-id-type-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {ID_TYPES.map((idt) => <SelectItem key={idt} value={idt} data-testid={`id-type-${idt.replace(/[^a-z]/gi, "").toLowerCase()}`}>{idt}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("customers.id_number")}</Label>
                <Input data-testid="customer-id-input" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t("common.address")}</Label>
              <Input data-testid="customer-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("cust.municipality")}</Label>
                <Input data-testid="customer-municipality-input" value={form.municipality} onChange={(e) => setForm({ ...form, municipality: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("cust.posto")}</Label>
                <Input data-testid="customer-posto-input" value={form.posto} onChange={(e) => setForm({ ...form, posto: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("cust.suco")}</Label>
                <Input data-testid="customer-suco-input" value={form.suco} onChange={(e) => setForm({ ...form, suco: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("cust.aldeia")}</Label>
                <Input data-testid="customer-aldeia-input" value={form.aldeia} onChange={(e) => setForm({ ...form, aldeia: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t("common.notes")}</Label>
              <Input data-testid="customer-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("customers.photo")}</Label>
              <div className="flex items-center gap-3 mt-1">
                {(photoUrl || editing?.photo_path) && (
                  <img src={photoUrl || `${API.defaults.baseURL}/shop/customers/${editing.id}/photo`} alt=""
                    data-testid="customer-photo-preview"
                    className="h-12 w-12 rounded-full object-cover border border-slate-200" />
                )}
                <Input data-testid="customer-photo-input" type="file" accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; setPhoto(f || null); setPhotoUrl(f ? URL.createObjectURL(f) : null); }}
                  className="text-xs" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button data-testid="customer-save-button" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <CustomerDetail customerId={viewing?.id} open={!!viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
